<script setup>
// Estúdio Social — aba FEED (pedido do social media da conta 1, 26/08):
// prévia de como o feed fica com os posts agendados — grade 3 colunas com
// as artes na ordem do calendário (mais recente no topo, como no Instagram).
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import socialAPI from 'dashboard/api/social';
import PostEditor from './PostEditor.vue';
import SocialNav from './SocialNav.vue';

const { t } = useI18n();

const isLoading = ref(true);
const posts = ref([]);
const editorRef = ref(null);

const fetchPosts = async () => {
  isLoading.value = true;
  try {
    const { data } = await socialAPI.productionPosts();
    posts.value = data || [];
  } catch {
    posts.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Ordem do feed: o post mais FUTURO aparece primeiro (é o topo do feed
// quando tudo for publicado). Sem data agendada vai pro fim.
const feedPosts = computed(() =>
  posts.value
    .filter(p => p.media_url && p.status !== 'failed')
    .sort((a, b) => {
      const da = a.scheduled_at || a.published_at || a.created_at;
      const db = b.scheduled_at || b.published_at || b.created_at;
      return new Date(db) - new Date(da);
    })
);

const formatDate = value =>
  value
    ? new Date(value).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
      })
    : '';

const openPost = id => editorRef.value?.open(id);

onMounted(fetchPosts);
</script>

<template>
  <div class="flex flex-col flex-1 overflow-auto bg-n-background">
    <div class="px-6 py-5 max-w-3xl w-full mx-auto space-y-5">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('SOCIAL_STUDIO.FEED.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-10">
            {{ t('SOCIAL_STUDIO.FEED.SUBTITLE') }}
          </p>
        </div>
        <SocialNav />
      </div>

      <div v-if="isLoading" class="flex justify-center py-12">
        <span
          class="i-lucide-loader-circle w-6 h-6 animate-spin text-n-slate-9"
        />
      </div>

      <div
        v-else-if="!feedPosts.length"
        class="py-16 text-center text-sm text-n-slate-10"
      >
        {{ t('SOCIAL_STUDIO.FEED.EMPTY') }}
      </div>

      <!-- Grade 3 colunas estilo Instagram -->
      <div v-else class="grid grid-cols-3 gap-1">
        <button
          v-for="post in feedPosts"
          :key="post.id"
          class="relative aspect-square overflow-hidden bg-n-alpha-2 group"
          @click="openPost(post.id)"
        >
          <img
            :src="post.media_url"
            :alt="post.title || ''"
            class="w-full h-full object-cover"
            loading="lazy"
          />
          <div
            class="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex flex-col items-center justify-center gap-1 opacity-0 group-hover:opacity-100"
          >
            <p
              v-if="post.title"
              class="px-2 text-xs text-white text-center line-clamp-2"
            >
              {{ post.title }}
            </p>
            <p class="text-[11px] text-white/80">
              {{ formatDate(post.scheduled_at || post.published_at) }}
            </p>
          </div>
          <span
            v-if="post.status !== 'published'"
            class="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full border border-white/60"
            :class="post.status === 'approved' ? 'bg-n-blue-9' : 'bg-n-amber-9'"
            :title="
              t(`SOCIAL_STUDIO.EDITOR.STATUS.${post.status.toUpperCase()}`)
            "
          />
        </button>
      </div>

      <PostEditor ref="editorRef" @saved="fetchPosts" />
    </div>
  </div>
</template>
